/**
 * rt-integrations.js — v1.0
 * Royal Toolbox facade for third-party app widgets.
 *
 * Centralizes every hack needed to make embedded app widgets behave, so
 * sections consume a simple API instead of re-implementing per-app workarounds.
 *
 * Usage:
 *   <script src="{{ 'rt-integrations.js' | asset_url }}" defer></script>
 *   RT.wishlist.mount(placeholder, { full: true|false, onReady: fn })
 *   RT.wishlist.isOn(productId) -> Promise<boolean> (resolves false if unavailable)
 *
 * Currently backed by Growave's wishlist custom element (open shadow root).
 */
window.RT = window.RT || {};

(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Wishlist (Growave)
   * ------------------------------------------------------------------ */

  var PLACEHOLDER_SELECTOR = '[data-rt-wishlist]';
  var POLL_INTERVAL_MS = 50;
  var POLL_TIMEOUT_MS = 5000; // give up if Growave never hydrates

  // Shadow-DOM recipe: make Growave's Svelte widget fill our host box exactly.
  // Everything here is a workaround for the widget's own layout opinions.
  //
  // Label visibility and icon gap read CSS custom properties rather than being
  // baked in. Custom properties inherit across the shadow boundary (verified
  // against Growave's widget), so a container query in the consuming section can
  // flip the button between icon-only and labelled at different card widths
  // WITHOUT re-mounting or re-injecting anything. The isFull argument only
  // supplies the default for callers that don't set the properties.
  function buildShadowRules(isFull) {
    var labelDefault = isFull ? 'block' : 'none';
    return [
      ':host { position: relative !important; }',
      '.gw-wl-add-to-wishlist-product-page-widget { position: absolute !important; inset: 0 !important; }',
      'button { position: absolute !important; inset: 0 !important; width: 100% !important; height: 100% !important; padding: 0 !important; margin: 0 !important; display: flex !important; align-items: center !important; justify-content: center !important; }',
      // Growave's class is __inner (two underscores). This previously read
      // _inner, so the rule never matched and --rt-wl-icon-gap did nothing.
      '.gw-wl-add-to-wishlist-btn__inner { display: flex !important; align-items: center !important; justify-content: center !important; padding: 0 !important; gap: var(--rt-wl-icon-gap, 0) !important; }',
      // Heart weight and glow, driven from the host. Stroke COLOUR is left
      // alone on purpose — Growave recolours it for the added state.
      '.gw-wl-icon { filter: var(--rt-wl-heart-glow, none) !important; transition: filter 0.15s ease !important; }',
      '.gw-wl-icon path { stroke-width: var(--rt-wl-heart-weight, 1.5px) !important; transition: stroke-width 0.15s ease !important; }',
      'span { display: var(--rt-wl-label-display, ' + labelDefault + ') !important;' +
        ' font-family: "Inknut Antiqua", serif !important;' +
        ' font-size: var(--rt-wl-label-size, 0.75rem) !important;' +
        ' letter-spacing: 0.12em !important;' +
        ' text-transform: uppercase !important; }'
    ].join(' ');
  }

  /**
   * Wait for Growave to hydrate a placeholder (shadow root + button), then
   * inject the layout-override <style> so Svelte re-renders can't undo it.
   * Bounded polling: stops after POLL_TIMEOUT_MS and reports failure.
   */
  function hydrate(placeholder, options, done) {
    var elapsed = 0;
    var timer = setInterval(function () {
      elapsed += POLL_INTERVAL_MS;
      var shadow = placeholder.shadowRoot;
      if (shadow && shadow.querySelector('button')) {
        clearInterval(timer);
        var s = document.createElement('style');
        s.textContent = buildShadowRules(!!options.full);
        shadow.appendChild(s);
        if (done) done(true);
        return;
      }
      if (elapsed >= POLL_TIMEOUT_MS) {
        clearInterval(timer);
        if (done) done(false);
      }
    }, POLL_INTERVAL_MS);
  }

  /**
   * Mount a wishlist button for a product.
   * @param {HTMLElement} placeholder  element carrying data-gw-product-id etc.
   * @param {{full?: boolean, onReady?: function(boolean)}} options
   */
  function mount(placeholder, options) {
    options = options || {};
    if (!placeholder) return;
    placeholder.setAttribute('data-rt-wishlist', '');
    hydrate(placeholder, options, options.onReady);
  }

  /**
   * Scan a root for un-mounted wishlist placeholders and mount them.
   * Sections call this once after render / after DOM reshuffling.
   */
  function mountAll(root, options) {
    (root || document).querySelectorAll(
      '.gw-wl-add-to-wishlist-placeholder:not(' + PLACEHOLDER_SELECTOR + ')'
    ).forEach(function (ph) {
      mount(ph, options);
    });
  }

  /** Best-effort state read. Resolves false when the widget isn't hydrated. */
  function isOn(productId) {
    return new Promise(function (resolve) {
      var ph = document.querySelector(
        '.gw-wl-add-to-wishlist-placeholder[data-gw-product-id="' + productId + '"]'
      );
      var btn = ph && ph.shadowRoot && ph.shadowRoot.querySelector('button');
      if (!btn) { resolve(false); return; }
      resolve(btn.classList.contains('--added') || btn.getAttribute('aria-pressed') === 'true');
    });
  }

  RT.wishlist = {
    mount: mount,
    mountAll: mountAll,
    isOn: isOn,
    /** Exposed so sections can re-check hydration after SPA-ish app re-renders. */
    hydrate: hydrate
  };
})();
